import { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { z } from 'zod';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useStore } from '@tanstack/react-form';
import { cn } from '@/lib/utils';
import { Label } from '@/common/components/ui/label';
import { Button } from '@/common/components/ui/button';
import { SelectItem } from '@/common/components/ui/select';
import { useAppForm } from '@/lib/form';
import { appFormOptions } from '@/common/form/appFormOptions';
import { requiredInput } from '@/common/form/requiredInput';
import { FormErrors } from '@/common/components/form-errors/FormErrors';
import { formatNumber } from '@/common/utils/formatNumber';
import { getActivePrice } from '../../utils/prices';
import type { Variant } from '../../services/schemas';
import type { BillingAddress } from '@/features/billing-addresses/services/schemas';

// ─── Schema ───────────────────────────────────────────────────────────────────

const activationFormSchema = z.object({
  billingFrequency: requiredInput(z.enum(['monthly', 'annual'])),
  billingContactId: requiredInput(z.coerce.number().int().positive()),
});

export type ActivateInput = {
  billingContact: number;
  billingFrequency: 'monthly' | 'annual';
  selectedVariantId: number;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getFromPrice(variant: Variant): { price: number } | null {
  const entry = getActivePrice(variant.priceSet);
  if (!entry) return null;
  return { price: Math.min(entry.priceMonthly, entry.priceAnnual / 12) };
}

function calcSavingsPercent(priceMonthly: number, priceAnnual: number): number {
  const annualMonthly = priceAnnual / 12;
  return priceMonthly > 0 ? Math.round(((priceMonthly - annualMonthly) / priceMonthly) * 100) : 0;
}

// ─── Component ────────────────────────────────────────────────────────────────

interface ProductActivationFormProps {
  variants: Variant[];
  billingAddresses: BillingAddress[];
  onActivate: (input: ActivateInput) => Promise<void>;
  onCancel: () => void;
  /** Opens the dialog for creating a billing address, shown when the user has none. */
  onAddBillingAddress: () => void;
}

export function ProductActivationForm({
  variants,
  billingAddresses,
  onActivate,
  onCancel,
  onAddBillingAddress,
}: ProductActivationFormProps) {
  const [selectedVariantId, setSelectedVariantId] = useState(variants[0]?.id ?? 0);
  const selectedVariant = variants.find((v) => v.id === selectedVariantId) ?? variants[0];

  const currentPrice = selectedVariant ? getActivePrice(selectedVariant.priceSet) : null;
  const annualSavingsPercent = currentPrice
    ? calcSavingsPercent(currentPrice.priceMonthly, currentPrice.priceAnnual)
    : 0;

  const form = useAppForm(
    appFormOptions({
      schema: activationFormSchema,
      defaultValues: {
        billingFrequency: 'monthly' as 'monthly' | 'annual' | null,
        billingContactId: billingAddresses[0]?.id ? String(billingAddresses[0].id) : null,
      },
      onSubmit: async ({ value }) => {
        await onActivate({
          billingContact: value.billingContactId,
          billingFrequency: value.billingFrequency,
          selectedVariantId,
        });
      },
    })
  );

  const selectedFrequency = useStore(form.store, (s) => s.values.billingFrequency);
  const selectedContactId = useStore(form.store, (s) => s.values.billingContactId);

  const hasBillingAddresses = billingAddresses.length > 0;

  // The first address is preselected in the default values, but the list is empty on mount
  // when the user has no address yet. Preselect as soon as one is added from here.
  useEffect(() => {
    const firstAddressId = billingAddresses[0]?.id;
    if (selectedContactId === null && firstAddressId !== undefined) {
      form.setFieldValue('billingContactId', String(firstAddressId));
    }
  }, [billingAddresses, selectedContactId, form]);

  return (
    <div className="rounded-xl border border-gray-200 p-4 space-y-4">
      <span className="text-xs font-bold uppercase tracking-wide text-gray-400">
        <Trans>Activate this product</Trans>
      </span>

      <form.AppForm>
        <form.FormElement>
          <div className="space-y-4">
            <FormErrors />

            {/* Variant selector */}
            {variants.length > 1 && (
              <div className="space-y-1.5">
                <Label>
                  <Trans>Variant</Trans>
                </Label>
                <div className="space-y-2">
                  {variants.map((v) => {
                    const fromPrice = getFromPrice(v);
                    const isSelected = selectedVariantId === v.id;
                    return (
                      <button
                        key={v.id}
                        type="button"
                        onClick={() => setSelectedVariantId(v.id)}
                        className={cn(
                          'w-full text-left flex items-center justify-between rounded-xl border px-3 py-3 text-sm font-medium transition-colors',
                          isSelected
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300'
                        )}
                      >
                        <span>{v.name}</span>
                        {fromPrice && (
                          <span className="text-xs font-normal opacity-75">
                            {t`from`}{' '}
                            {formatNumber(fromPrice.price, 'currency', {
                              currency: 'CZK',
                            })}{' '}
                            <Trans>/ mo</Trans>
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Billing frequency */}
            <form.Field name="billingFrequency">
              {(field) => (
                <div className="space-y-1.5">
                  <Label>
                    <Trans>Billing frequency</Trans>
                  </Label>
                  <div className="grid grid-cols-2 gap-2">
                    {(['monthly', 'annual'] as const).map((freq) => {
                      const price = currentPrice
                        ? freq === 'monthly'
                          ? currentPrice.priceMonthly
                          : currentPrice.priceAnnual
                        : null;
                      return (
                        <button
                          key={freq}
                          type="button"
                          onClick={() => field.handleChange(freq)}
                          className={cn(
                            'rounded-xl border px-3 py-3 text-sm font-medium transition-colors flex flex-col items-center gap-0.5',
                            field.state.value === freq
                              ? 'border-blue-500 bg-blue-50 text-blue-700'
                              : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300'
                          )}
                        >
                          <span>{freq === 'monthly' ? t`Monthly` : t`Annual`}</span>
                          {price !== null && price !== undefined && (
                            <span className="text-xs font-normal opacity-75">
                              {formatNumber(price, 'currency', { currency: 'CZK' })} /{' '}
                              {freq === 'monthly' ? t`mo` : t`yr`}
                            </span>
                          )}
                          {freq === 'annual' && annualSavingsPercent > 0 && (
                            <span className="mt-0.5 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">
                              <Trans>Save {annualSavingsPercent}%</Trans>
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </form.Field>

            {/* Billing address */}
            {!hasBillingAddresses ? (
              <div className="space-y-1.5">
                <Label className="text-destructive">
                  <Trans>Billing address</Trans>
                  <span className="ml-0.5">*</span>
                </Label>
                <div className="space-y-2 rounded-xl border border-destructive bg-destructive/5 px-4 py-3">
                  <p className="text-sm font-medium text-destructive">
                    <Trans>A billing address is required to activate this product.</Trans>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    <Trans>
                      The address of your organization is not used for billing — add a billing
                      address to continue.
                    </Trans>
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    onClick={onAddBillingAddress}
                  >
                    <Plus size={14} />
                    <Trans>Add billing address</Trans>
                  </Button>
                </div>
              </div>
            ) : (
              <form.AppField name="billingContactId">
                {(field) => (
                  <field.SelectField label={<Trans>Billing address</Trans>} required>
                    {billingAddresses.map((a) => (
                      <SelectItem key={a.id} value={String(a.id)}>
                        {a.name ?? a.street} — {a.city}
                      </SelectItem>
                    ))}
                  </field.SelectField>
                )}
              </form.AppField>
            )}

            {/* Price summary */}
            {currentPrice && selectedFrequency && (
              <div className="flex items-center justify-between rounded-xl bg-gray-50 border border-gray-200 px-4 py-3">
                <div>
                  <span className="text-[10px] font-bold uppercase tracking-wide text-gray-400">
                    <Trans>Total</Trans>
                  </span>
                  <div className="text-sm font-semibold text-gray-900 mt-0.5">
                    {selectedFrequency === 'monthly' ? (
                      <>
                        {formatNumber(currentPrice.priceMonthly, 'currency', {
                          currency: 'CZK',
                        })}{' '}
                        <Trans>/ mo</Trans>
                      </>
                    ) : (
                      <>
                        {formatNumber(currentPrice.priceAnnual, 'currency', {
                          currency: 'CZK',
                        })}{' '}
                        <Trans>/ yr</Trans>
                      </>
                    )}
                  </div>
                </div>
                <span className="text-xs text-gray-400">
                  <Trans>excl. VAT</Trans>
                </span>
              </div>
            )}

            {/* Form actions */}
            <form.Subscribe selector={(s) => s.canSubmit && hasBillingAddresses}>
              {(canSubmit) => (
                <div className="space-y-2 pt-2">
                  {!canSubmit && (
                    <p className="text-xs text-destructive sm:text-right">
                      <Trans>Add a billing address to activate this product.</Trans>
                    </p>
                  )}
                  <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3">
                    <Button type="button" variant="outline" onClick={onCancel}>
                      <Trans>Cancel</Trans>
                    </Button>
                    <Button type="submit" disabled={!canSubmit}>
                      <Trans>Activate product</Trans>
                    </Button>
                  </div>
                </div>
              )}
            </form.Subscribe>
          </div>
        </form.FormElement>
      </form.AppForm>
    </div>
  );
}
